import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// Cache global para los colores
let colorSettingsCache = null;
let lastFetchTime = null;
let isFetching = false;
let retryCount = 0;
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
const MAX_RETRIES = 3;
const RETRY_DELAY = 5000; // 5 segundos

const ColorSettingsContext = createContext();

export function ColorSettingsProvider({ children }) {
  const [colorSettings, setColorSettings] = useState(colorSettingsCache || {});
  const [loading, setLoading] = useState(!colorSettingsCache);
  const [error, setError] = useState(null);
  const subscriptionRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const fetchColorSettings = useCallback(async (force = false) => {
    // Evitar múltiples peticiones simultáneas
    if (isFetching && !force) {
      return;
    }

    // Si forzamos actualización, ignorar caché
    if (force) {
      // Resetear caché para forzar nueva carga
      colorSettingsCache = null;
      lastFetchTime = null;
    }
    // Si tenemos caché y no ha expirado, y no forzamos actualización, usar caché
    else if (colorSettingsCache && lastFetchTime && (Date.now() - lastFetchTime < CACHE_DURATION)) {
      setColorSettings(colorSettingsCache);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      isFetching = true;
      setLoading(true);
      setError(null);
      
      // Añadir timeout para evitar que la consulta se quede colgada
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout')), 10000); // 10 segundos timeout
      });

      const fetchPromise = supabase
        .from('app_settings')
        .select('color_mappings')
        .limit(1);

      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);

      if (error) throw error;

      if (data && data.length > 0) {
        // Convertir el formato antiguo al nuevo si es necesario
        const mappings = data[0].color_mappings || {};
        const convertedMappings = {};
        
        for (const [key, value] of Object.entries(mappings)) {
          convertedMappings[key] = typeof value === 'string' 
            ? { hex: value, description: key, active: true } 
            : { ...value, active: value.active !== false }; // Por defecto activo si no existe
        }
        
        // Actualizar caché
        colorSettingsCache = convertedMappings;
        lastFetchTime = Date.now();
        retryCount = 0; // Reset retry count on success
        
        setColorSettings(convertedMappings);
      }
    } catch (error) {
      console.error('Error fetching color settings:', error);
      setError(error);
      
      // En caso de error, usar caché si existe, sino establecer un mapeo por defecto
      setColorSettings(colorSettingsCache || {});
    } finally {
      setLoading(false);
      isFetching = false;
    }
  }, []);

  // Método para agregar un nuevo color
  const addColor = useCallback(async (colorId, colorData) => {
    try {
      const newColorSettings = {
        ...colorSettings,
        [colorId]: {
          hex: colorData.hex || '#000000',
          description: colorData.description || `Color ${colorId}`,
          description_zh: colorData.description_zh || '',
          active: colorData.active !== false
        }
      };

      // Actualizar estado local inmediatamente para UI responsiva
      setColorSettings(newColorSettings);
      
      // Actualizar caché
      colorSettingsCache = newColorSettings;
      lastFetchTime = Date.now();

      // Guardar en base de datos
      const { error } = await supabase
        .from('app_settings')
        .update({ color_mappings: newColorSettings })
        .eq('id', (await getSettingsId()));

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error adding color:', error);
      // Revertir cambios en caso de error
      setColorSettings(colorSettings);
      colorSettingsCache = colorSettings;
      return { success: false, error };
    }
  }, [colorSettings]);

  // Método para actualizar un color existente
  const updateColor = useCallback(async (colorId, updates) => {
    try {
      const updatedColorSettings = {
        ...colorSettings,
        [colorId]: {
          ...colorSettings[colorId],
          ...updates
        }
      };

      // Actualizar estado local inmediatamente
      setColorSettings(updatedColorSettings);
      
      // Actualizar caché
      colorSettingsCache = updatedColorSettings;
      lastFetchTime = Date.now();

      // Guardar en base de datos
      const { error } = await supabase
        .from('app_settings')
        .update({ color_mappings: updatedColorSettings })
        .eq('id', (await getSettingsId()));

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error updating color:', error);
      // Revertir cambios en caso de error
      setColorSettings(colorSettings);
      colorSettingsCache = colorSettings;
      return { success: false, error };
    }
  }, [colorSettings]);

  // Método para eliminar un color (solo colores 16+)
  const deleteColor = useCallback(async (colorId) => {
    if (parseInt(colorId) <= 15) {
      return { success: false, error: 'No se pueden eliminar colores del sistema (1-15)' };
    }

    try {
      const newColorSettings = { ...colorSettings };
      delete newColorSettings[colorId];

      // Actualizar estado local
      setColorSettings(newColorSettings);
      
      // Actualizar caché
      colorSettingsCache = newColorSettings;
      lastFetchTime = Date.now();

      // Guardar en base de datos
      const { error } = await supabase
        .from('app_settings')
        .update({ color_mappings: newColorSettings })
        .eq('id', (await getSettingsId()));

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting color:', error);
      // Revertir cambios en caso de error
      setColorSettings(colorSettings);
      colorSettingsCache = colorSettings;
      return { success: false, error };
    }
  }, [colorSettings]);

  // Método para obtener solo colores activos
  const getActiveColors = useCallback(() => {
    return Object.entries(colorSettings)
      .filter(([_, colorData]) => colorData.active)
      .reduce((acc, [colorId, colorData]) => {
        acc[colorId] = colorData;
        return acc;
      }, {});
  }, [colorSettings]);

  // Método para obtener el siguiente ID de color disponible
  const getNextColorId = useCallback(() => {
    const existingIds = Object.keys(colorSettings).map(Number).filter(id => !isNaN(id));
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 15;
    return (maxId + 1).toString();
  }, [colorSettings]);

  // Método para verificar si se puede agregar más colores
  const canAddMoreColors = useCallback(() => {
    return Object.keys(colorSettings).length < 50;
  }, [colorSettings]);

  // Método para obtener estadísticas de colores
  const getColorStats = useCallback(() => {
    const total = Object.keys(colorSettings).length;
    const active = Object.values(colorSettings).filter(color => color.active).length;
    const available = 50 - total;
    
    return { total, active, available };
  }, [colorSettings]);

  // Función auxiliar para obtener el ID de configuración
  const getSettingsId = async () => {
    if (colorSettingsCache && colorSettingsCache._settingsId) {
      return colorSettingsCache._settingsId;
    }

    const { data, error } = await supabase
      .from('app_settings')
      .select('id')
      .limit(1);

    if (error || !data || data.length === 0) {
      throw new Error('No se pudo obtener el ID de configuración');
    }

    const settingsId = data[0].id;
    
    // Guardar en caché
    if (colorSettingsCache) {
      colorSettingsCache._settingsId = settingsId;
    }
    
    return settingsId;
  };

  useEffect(() => {
    // Cargar colores de forma segura con manejo de errores robusto
    const loadColors = async () => {
      try {
        // Primero verificar si la tabla app_settings tiene datos
        const { data, error } = await supabase
          .from('app_settings')
          .select('id')
          .limit(1);

        if (error) {
          console.log('app_settings no disponible, usando configuración por defecto');
          setColorSettings({});
          setLoading(false);
          return;
        }

        if (!data || data.length === 0) {
          console.log('app_settings vacía; esperando configuración inicial de un administrador');
          setColorSettings({});
          setLoading(false);
          return;
        }

        // Ahora cargar los colores normalmente
        setTimeout(() => {
          fetchColorSettings();
        }, 100);

      } catch (error) {
        console.error('Error verificando app_settings:', error);
        setColorSettings({});
        setLoading(false);
      }
    };

    loadColors();

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [fetchColorSettings]);

  const value = {
    colorSettings,
    loading,
    error,
    refetch: () => fetchColorSettings(true),
    // Nuevos métodos para gestión de colores
    addColor,
    updateColor,
    deleteColor,
    getActiveColors,
    getNextColorId,
    canAddMoreColors,
    getColorStats
  };

  return (
    <ColorSettingsContext.Provider value={value}>
      {children}
    </ColorSettingsContext.Provider>
  );
}

export function useColorSettings() {
  const context = useContext(ColorSettingsContext);
  if (context === undefined) {
    throw new Error('useColorSettings must be used within a ColorSettingsProvider');
  }
  return context;
} 
